import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../common/Logo';

const Layout = () => {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => {
    return location.pathname === `/admin${path}` || location.pathname.startsWith(`/admin${path}/`);
  };

  const navigation = [
    { name: 'Dashboard', href: '', icon: 'fas fa-tachometer-alt', category: 'admin' },
    
    // Website Pages Section
    { name: 'Home Page', href: '/pages/home', icon: 'fas fa-home', category: 'pages' },
    { 
      name: 'About Us', 
      href: '/pages/about', 
      icon: 'fas fa-info-circle', 
      category: 'pages',
      hasDropdown: true,
      subPages: [
        { name: 'Vision & Mission', href: '/pages/about/vision-mission' },
        { name: 'Chairman Message', href: '/pages/about/chairman-message' },
        { name: 'Secretary Message', href: '/pages/about/secretary-message' },
        { name: 'Joint Secretary Message', href: '/pages/about/joint-secretary-message' },
        { name: 'Director Message', href: '/pages/about/director-message' },
        { name: 'Approvals & Affiliations', href: '/pages/about/approvals-affiliations' }
      ]
    },
    { name: 'Academics', href: '/pages/academics', icon: 'fas fa-graduation-cap', category: 'pages' },
    { name: 'Admissions', href: '/pages/admissions', icon: 'fas fa-user-plus', category: 'pages' },
    { name: 'Training & Placements', href: '/pages/placements', icon: 'fas fa-briefcase', category: 'pages' },
    { name: 'Research & Development', href: '/pages/research', icon: 'fas fa-flask', category: 'pages' },
    { name: 'Contact Page', href: '/pages/contact', icon: 'fas fa-map-marker-alt', category: 'pages' },
    
    // Content Management Section
    { name: 'Announcements', href: '/announcements', icon: 'fas fa-bullhorn', category: 'content' },
    { name: 'Events Page Content', href: '/events-page', icon: 'fas fa-edit', category: 'content' },
    { name: 'Contact Messages', href: '/contacts', icon: 'fas fa-envelope', category: 'content' },
    { name: 'Admission Enquiries', href: '/admission-enquiries', icon: 'fas fa-user-graduate', category: 'content' },
    
    // Admin Section
    ...(user?.role === 'admin' ? [
      { name: 'Users Management', href: '/users', icon: 'fas fa-users', category: 'admin' },
      { name: 'Grievances', href: '/grievances', icon: 'fas fa-exclamation-circle', category: 'admin' },
      { name: 'SMTP Settings', href: '/smtp-settings', icon: 'fas fa-envelope-open-text', category: 'admin' }
    ] : [])
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">
          <i className="fas fa-spinner fa-spin mr-2"></i>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-surface font-sans">
      <header className="bg-white border-b border-border_gray shadow-sm">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient"></div>
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="normal" />
            <div className="border-l border-border_gray pl-4">
              <h1 className="text-xl font-sans font-bold text-slate">Admin Panel</h1>
              <p className="text-xs text-gray-500 uppercase tracking-widest">Management System</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate">
              Welcome, <strong className="font-semibold">{user?.username}</strong>
              <span className="ml-2 px-2 py-1 bg-crimson text-white text-xs rounded font-medium">
                {user?.role}
              </span>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-gray-100 hover:bg-crimson hover:text-white text-slate rounded-md transition-all duration-200 flex items-center gap-2 font-medium"
              title="Logout"
            >
              <i className="fas fa-sign-out-alt"></i>
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden" style={{height: 'calc(100vh - 80px)'}}>
        <aside className="w-64 bg-white border-r border-border_gray shadow-sm flex flex-col">
          <nav className="flex-1 overflow-y-auto py-4" style={{scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9'}}>
            <div className="px-4 space-y-6 pb-4">
              {/* Dashboard */}
              <div>
                <ul className="space-y-1">
                  {navigation.filter(item => item.category === 'admin' && item.href === '').map((item) => (
                    <li key={item.name}>
                      <Link
                        to={`/admin${item.href}`}
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
                          ${isActive(item.href) 
                            ? 'bg-crimson text-white shadow-md' 
                            : 'text-slate hover:bg-gray-50 hover:text-crimson'
                          }
                        `}
                      >
                        <i className={`${item.icon} w-5`}></i>
                        <span>{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Website Pages */}
              <div>
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Website Pages
                </h3>
                <ul className="space-y-1">
                  {navigation.filter(item => item.category === 'pages').map((item) => (
                    <li key={item.name}>
                      {item.hasDropdown ? (
                        <div>
                          <button
                            onClick={() => setShowAboutDropdown(!showAboutDropdown)}
                            className={`
                              w-full flex items-center justify-between px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                              ${(isActive(item.href) || item.subPages?.some(sub => isActive(sub.href))) 
                                ? 'bg-crimson text-white shadow-md' 
                                : 'text-slate hover:bg-gray-50 hover:text-crimson'
                              }
                            `}
                          >
                            <div className="flex items-center gap-3">
                              <i className={`${item.icon} w-4 text-xs`}></i>
                              <span className="text-xs">{item.name}</span>
                            </div>
                            <i className={`fas fa-chevron-down text-xs transition-transform ${showAboutDropdown ? 'rotate-180' : ''}`}></i>
                          </button>
                          {showAboutDropdown && (
                            <ul className="ml-6 mt-1 space-y-1">
                              {item.subPages.map((subItem) => (
                                <li key={subItem.name}>
                                  <Link
                                    to={`/admin${subItem.href}`}
                                    className={`
                                      block px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200
                                      ${isActive(subItem.href) 
                                        ? 'bg-crimson text-white shadow-sm' 
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-crimson'
                                      }
                                    `}
                                  >
                                    → {subItem.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={`/admin${item.href}`}
                          className={`
                            flex items-center gap-3 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                            ${isActive(item.href) 
                              ? 'bg-crimson text-white shadow-md' 
                              : 'text-slate hover:bg-gray-50 hover:text-crimson'
                            }
                          `}
                        >
                          <i className={`${item.icon} w-4 text-xs`}></i>
                          <span className="text-xs">{item.name}</span>
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Content Management */}
              <div>
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Content Management
                </h3>
                <ul className="space-y-1">
                  {navigation.filter(item => item.category === 'content').map((item) => (
                    <li key={item.name}>
                      <Link
                        to={`/admin${item.href}`}
                        className={`
                          flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
                          ${isActive(item.href) 
                            ? 'bg-crimson text-white shadow-md' 
                            : 'text-slate hover:bg-gray-50 hover:text-crimson'
                          }
                        `}
                      >
                        <i className={`${item.icon} w-5`}></i>
                        <span>{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Admin Functions */}
              {user?.role === 'admin' && (
                <div>
                  <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Administration
                  </h3>
                  <ul className="space-y-1">
                    {navigation.filter(item => item.category === 'admin' && item.href !== '').map((item) => (
                      <li key={item.name}>
                        <Link
                          to={`/admin${item.href}`}
                          className={`
                            flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
                            ${isActive(item.href) 
                              ? 'bg-crimson text-white shadow-md' 
                              : 'text-slate hover:bg-gray-50 hover:text-crimson'
                            }
                          `}
                        >
                          <i className={`${item.icon} w-5`}></i>
                          <span>{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto bg-surface">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;